import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { money } from "@/lib/constants";
import { amountInWords } from "@/lib/words";
import { useCamp } from "@/context/SettingsContext";
import PrintLogo from "@/components/print/PrintLogo";
import PrintFooter from "@/components/print/PrintFooter";
import { Button } from "@/components/ui/button";
import DemoStamp from "@/components/DemoStamp";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-6 py-1.5 border-b border-slate-200 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-right">{value || "—"}</span>
    </div>
  );
}

export default function PrintDeposit() {
  const { t, lang } = useLang();
  const { camp } = useCamp();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [dep, setDep] = useState(null);

  useEffect(() => { api.get(`/cash/deposits/${id}`).then(({ data }) => setDep(data)).catch(() => {}); }, [id]);
  useEffect(() => {
    if (!dep) return;
    const timer = setTimeout(() => window.print(), 500);
    return () => clearTimeout(timer);
  }, [dep]);

  if (!dep) return <div className="p-8 text-sm text-muted-foreground">…</div>;

  return (
    <div className="print-page bg-white text-slate-900 mx-auto max-w-[560px] p-8 print:p-0" data-testid="print-deposit">
      <DemoStamp />
      <div className="flex justify-end print:hidden mb-3">
        <Button size="sm" onClick={() => window.print()} data-testid="print-deposit-now"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
      </div>
      <div className="flex items-center gap-3">
        <PrintLogo camp={camp} testid="print-deposit-logo" />
        <div>
          <h1 className="font-display font-extrabold text-xl">{camp.camp_name}</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("cash_deposit_receipt")}</p>
        </div>
      </div>
      <p className="mt-2 font-display font-extrabold text-2xl tabular-nums" data-testid="print-deposit-number">{dep.number || "—"}</p>
      {dep.cancelled && <p className="text-sm font-semibold text-rose-600">{t("deposit_cancelled")}: {dep.cancel_reason}</p>}

      <p className="mt-6 font-display font-extrabold text-3xl tabular-nums" data-testid="print-deposit-amount">{money(dep.amount)}</p>
      <p className="text-sm italic text-slate-600" data-testid="print-deposit-words">{amountInWords(dep.amount)}</p>

      <div className="mt-6">
        <Row label={t("date")} value={dep.date} />
        <Row label={t("cash_deposit_by")} value={dep.by} />
        <Row label={t("cash_deposit_account")} value={dep.bank_account} />
        <Row label={t("cash_deposit_origin")} value={dep.source === "closure" ? `${t("admin_cashclose")} ${dep.closure_date}` : t("cash_deposit_interim")} />
        <Row label={t("note")} value={dep.note} />
        <Row label={t("logged_in_as")} value={dep.created_by} />
      </div>

      <div className="mt-12 grid grid-cols-2 gap-8 text-[11px] text-slate-500">
        <div className="border-t border-slate-400 pt-2">{t("cash_sign_staff")}</div>
        <div className="border-t border-slate-400 pt-2">{t("cash_sign_bank")}</div>
      </div>

      <PrintFooter camp={camp} lang={lang} vatLabel={t("setup_vat_number")} />
    </div>
  );
}
